import React, {
  useState,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from "react";
import { Popconfirm, message, Tooltip, Input, Modal } from "antd";
import {
  ClearOutlined,
  LoadingOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import {
  XAdkSenderProps,
  XAdkSenderHandle,
  ServerFile,
  LocalFile,
  ActionsComponents,
  FileValidator,
  UploadFileResult,
  MentionTag,
  XAdkSenderSubmitData,
  XAdkSenderSubmitFormat,
  XAdkSenderSubmitPart,
} from "@/types";
import { useStyles } from "./styles";
import FileGallery from "../FileGallery";
import QuickList from "./QuickList";
import InputTags from "./InputTags";
import InlineTagInput, { InlineTagInputRef } from "./InlineTagInput";
import { getExt } from "@/utils/file";

const XAdkSender = forwardRef<XAdkSenderHandle, XAdkSenderProps>(
  (
    {
      clearBtnShow = true,
      draggable = false,
      onFileSelect,
      loading = false,
      disabled = false,
      value: controlledValue,
      defaultValue = "",
      uploadRequest = () => {},
      onClear,
      onChange,
      onSubmit,
      onStop,
      onFilesChange,
      onUploadSuccess,
      onUploadError,
      maxFileSize = 10,
      placeholder = "",
      validators: customValidators = [],
      allowedFileTypes = [],
      maxFiles = Infinity,
      sendButtonRender,
      suffix,
      header,
      prefix,
      footer,
      footerLeft,
      quickKeys = [],
      inlineTags = false,
      renderMention,
      renderTag,
      mentions: controlledMentions,
      tags: controlledTags,
      defaultMentions,
      defaultTags = [],
      defaultInputParts,
      onMentionsChange,
      onTagsChange,
      onQuickKeyTrigger,
      submitFormat = "text",
      formatMention,
      formatTag,
      transformSubmitData,
    },
    ref,
  ) => {
    const styles = useStyles();
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState<string>(defaultValue);
    const value = isControlled ? controlledValue : internalValue;
    const setValue = (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };

    // mentions 支持受控/非受控两种模式；inlineTags 打开时会和输入内容混排。
    const resolvedControlledMentions = controlledMentions ?? controlledTags;
    const initialMentions = defaultMentions ?? defaultTags;
    const isMentionsControlled = resolvedControlledMentions !== undefined;
    const [internalMentions, setInternalMentions] =
      useState<MentionTag[]>(initialMentions);
    const mentions = isMentionsControlled
      ? resolvedControlledMentions!
      : internalMentions;
    const setMentions = (newMentions: MentionTag[]) => {
      if (!isMentionsControlled) {
        setInternalMentions(newMentions);
      }
      onMentionsChange?.(newMentions);
      onTagsChange?.(newMentions);
    };

    const [files, setFiles] = useState<LocalFile[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<any>(null);
    const inlineInputRef = useRef<InlineTagInputRef>(null);

    // 快捷键弹层状态：记录触发字符、关键词，以及触发字符在纯文本中的位置。
    // SDK 不内置搜索框；业务方可在 onQuickKeyTrigger 中根据 keyword 自行更新 quickKeys.options。
    const [activeQuickKey, setActiveQuickKey] = useState<string | null>(null);
    const [quickKeyKeyword, setQuickKeyKeyword] = useState("");
    const [quickKeyTriggerIdx, setQuickKeyTriggerIdx] = useState(-1);
    const [quickKeyActiveIndex, setQuickKeyActiveIndex] = useState(0);

    // 生成唯一ID
    const generateId = () => {
      return Date.now().toString(36) + Math.random().toString(36).substr(2);
    };

    const normalizeUploadResponse = (response: any): UploadFileResult => {
      const data = response?.data ?? response;
      return (Array.isArray(data) ? data[0] : data) ?? {};
    };

    const escapeXml = (text: string) =>
      text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");

    const escapeMarkdownLabel = (text: string) =>
      text.replace(/\\/g, "\\\\").replace(/\]/g, "\\]");

    const getMentionSubmitValue = (mention: MentionTag) =>
      String(
        mention.value ??
          mention.uri ??
          mention.data?.uri ??
          mention.data?.value ??
          mention.data?.name ??
          mention.targetId ??
          mention.displayName ??
          mention.label ??
          "",
      );

    const defaultFormatMention = (
      mention: MentionTag,
      format: Exclude<XAdkSenderSubmitFormat, "structured">,
      index: number,
    ) => {
      const value = getMentionSubmitValue(mention);
      if (format === "xml") {
        return `<mention type="${escapeXml(mention.type)}" id="${escapeXml(mention.id)}" label="${escapeXml(mention.label)}" value="${escapeXml(value)}" />`;
      }

      if (format === "markdown") {
        const href =
          mention.uri ||
          (value.includes("://")
            ? value
            : `xadk-mention://${encodeURIComponent(mention.type)}/${encodeURIComponent(mention.id)}`);
        return `[@${escapeMarkdownLabel(mention.displayName || mention.label)}](${href})`;
      }

      return `@${mention.displayName || value || `mention-${index + 1}`}`;
    };

    const serializeMention = (
      mention: MentionTag,
      format: Exclude<XAdkSenderSubmitFormat, "structured">,
      index: number,
    ) =>
      formatMention?.(mention, { format, index }) ??
      formatTag?.(mention, { format, index }) ??
      defaultFormatMention(mention, format, index);

    const getSubmitParts = useCallback((): XAdkSenderSubmitPart[] => {
      if (inlineTags) {
        const parts = inlineInputRef.current?.getParts() || [];
        if (parts.length > 0) return parts;
      }

      const textParts: XAdkSenderSubmitPart[] = value ? [{ type: "text", text: value }] : [];
      const mentionParts = mentions.map((mention) => ({
        type: "mention" as const,
        mention,
      }));
      return [...mentionParts, ...textParts];
    }, [inlineTags, mentions, value]);

    const serializeParts = (
      parts: XAdkSenderSubmitPart[],
      format: XAdkSenderSubmitFormat,
    ) => {
      if (format === "structured") {
        return parts.reduce(
          (content, part) => content + (part.type === "text" ? part.text : ""),
          "",
        );
      }

      let tagIndex = 0;
      return parts
        .map((part) => {
          if (part.type === "text") return part.text;
          const result = serializeMention(part.mention, format, tagIndex);
          tagIndex += 1;
          return result;
        })
        .join("");
    };

    const createSubmitData = (formattedFiles: ServerFile[]): XAdkSenderSubmitData => {
      const parts = getSubmitParts();
      const rawText = parts.reduce(
        (content, part) => content + (part.type === "text" ? part.text : ""),
        "",
      );
      const standardData: Omit<XAdkSenderSubmitData, "payload"> = {
        text: serializeParts(parts, submitFormat),
        rawText,
        format: submitFormat,
        parts,
        files: formattedFiles,
        mentions,
        tags: mentions,
      };

      return {
        ...standardData,
        payload: transformSubmitData?.(standardData),
      };
    };

    // 检查文件类型
    const checkFileType = (file: File): boolean => {
      if (allowedFileTypes?.length > 0) {
        const ext = getExt(file);

        return allowedFileTypes.some((type) => {
          if (type.includes("/")) {
            if (type.endsWith("/*")) {
              return file.type.startsWith(type.split("/")[0]);
            }
            return file.type === type;
          }

          if (type.startsWith(".")) {
            return ext === type.slice(1);
          }

          return false;
        });
      }
      return true;
    };

    // 重复文件检测
    const isDuplicateFile = (file: File, files: LocalFile[]) => {
      return files.some((f) => f.name === file.name && f.size === file.size);
    };

    // 内置校验器集合
    const builtInValidators: FileValidator[] = [
      (_file, { files }) => {
        if (files.length >= maxFiles) {
          return `最多只能上传 ${maxFiles} 个文件`;
        }
        return null;
      },
      (file) => {
        const maxSize = maxFileSize * 1024 * 1024;
        if (file.size > maxSize) {
          return `文件大小不能超过 ${maxFileSize}MB`;
        }
        return null;
      },
      (file) => {
        if (!checkFileType(file)) {
          return "不支持的文件类型";
        }
        return null;
      },
      (file, { files }) => {
        if (isDuplicateFile(file, files)) {
          return "文件已存在";
        }
        return null;
      },
    ];

    const allValidators = [...builtInValidators, ...customValidators];

    // 验证文件
    const validateFile = (file: File): { valid: boolean; message?: string } => {
      for (const validator of allValidators) {
        const error = validator(file, { files });
        if (error) {
          return { valid: false, message: error };
        }
      }
      return { valid: true };
    };

    // 处理文件选择
    const handleFileSelect = useCallback(
      (selectedFiles: File[]) => {
        const fileArray = Array.from(selectedFiles);

        if (files.length + fileArray.length > maxFiles) {
          message.error(`最多只能上传 ${maxFiles} 个文件`);
          return;
        }

        const validFiles: LocalFile[] = [];

        fileArray.forEach((file) => {
          const validation = validateFile(file);

          if (validation.valid) {
            validFiles.push({
              id: generateId(),
              uid: generateId(),
              name: file.name,
              size: file.size,
              type: file.type,
              file,
              progress: 0,
              status: "pending",
              response: null,
            });
          } else {
            message.error(validation.message);
          }
        });

        if (validFiles.length > 0) {
          setFiles((prev) => {
            const next = [...prev, ...validFiles];
            onFilesChange?.(next);
            return next;
          });
          uploadFiles(validFiles);
        }
      },
      [files, maxFiles, maxFileSize, allowedFileTypes],
    );

    // 暴露给业务工具栏调用的命令式能力；外部 Popover 选择标签时可插入到当前光标位置。
    useImperativeHandle(
      ref,
      () => ({
        addFiles: (rawFiles: File[]) => {
          handleFileSelect(rawFiles);
        },
        insertMention: (mention: MentionTag) => {
          if (inlineTags) {
            inlineInputRef.current?.insertMention(mention);
            return;
          }
          setMentions([...mentions, mention]);
        },
        insertTag: (mention: MentionTag) => {
          if (inlineTags) {
            inlineInputRef.current?.insertMention(mention);
            return;
          }
          setMentions([...mentions, mention]);
        },
        focus: () => {
          inlineTags ? inlineInputRef.current?.focus() : textareaRef.current?.focus();
        },
      }),
      [handleFileSelect, inlineTags, setMentions, mentions],
    );

    // 上传文件
    const uploadFiles = async (fileList: LocalFile[]) => {
      for (const localFile of fileList) {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === localFile.id ? { ...f, status: "uploading" } : f,
          ),
        );

        try {
          await uploadRequest({
            file: localFile.file,
            onProgress: (e) => {
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === localFile.id ? { ...f, progress: e.percent } : f,
                ),
              );
            },
            onSuccess: (response) => {
              const responseData = normalizeUploadResponse(response);
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === localFile.id
                    ? {
                        ...f,
                        status: "success",
                        progress: 100,
                        response,
                        fileId: responseData.fileId || responseData.id,
                        tempUrl:
                          responseData.tempUrl ||
                          responseData.fileUrl ||
                          responseData.url,
                      }
                    : f,
                ),
              );

              onUploadSuccess?.({
                ...localFile,
                status: "success",
                response,
                fileId: responseData.fileId || responseData.id,
                tempUrl:
                  responseData.tempUrl ||
                  responseData.fileUrl ||
                  responseData.url,
              });
            },
            onError: (error) => {
              setFiles((prev) =>
                prev.map((f) =>
                  f.id === localFile.id
                    ? {
                        ...f,
                        status: "error",
                        errorMessage: error.message,
                      }
                    : f,
                ),
              );
              onUploadError?.(localFile, error);
            },
          });
        } catch (error) {
          console.error("上传处理错误:", error);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === localFile.id
                ? {
                    ...f,
                    status: "error",
                    errorMessage:
                      error instanceof Error ? error.message : "上传处理失败",
                  }
                : f,
            ),
          );
          message.error(`${localFile.name} 上传失败`);
        }
      }
    };

    // 删除文件
    const handleRemoveFile = (id: string) => {
      setFiles((prev) => {
        const next = prev.filter((file) => file.id !== id);
        onFilesChange?.(next);
        return next;
      });
    };

    // 处理拖拽事件
    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggable) {
          setIsDragOver(true);
        }
      },
      [draggable],
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggable) {
          setIsDragOver(false);
        }
      },
      [draggable],
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (
          draggable &&
          e.dataTransfer.files &&
          e.dataTransfer.files.length > 0
        ) {
          // 内核不再自行处理文件，交给外层（DefaultLayout/业务）通过 onFileSelect 决定
          onFileSelect?.(Array.from(e.dataTransfer.files));
        }
      },
      [draggable, onFileSelect],
    );

    // 删除标签
    const handleRemoveMention = useCallback(
      (id: string) => {
        setMentions(mentions.filter((item) => item.id !== id));
      },
      [mentions, setMentions],
    );

    // 查找当前活跃的快捷键配置
    const activeQuickKeyConfig = useMemo(() => {
      if (!activeQuickKey) return null;
      return quickKeys.find((qk) => qk.key === activeQuickKey) || null;
    }, [activeQuickKey, quickKeys]);

    const activeQuickKeyOptionsLength = activeQuickKeyConfig?.options.length || 0;

    useEffect(() => {
      setQuickKeyActiveIndex(0);
    }, [activeQuickKey]);

    useEffect(() => {
      if (activeQuickKeyOptionsLength <= 0) {
        setQuickKeyActiveIndex(0);
        return;
      }

      // 业务方可能在 onQuickKeyTrigger 中动态更新 options；
      // 这里只做越界归一化，不重置有效索引，避免键盘高亮移动后又跳回第一项。
      setQuickKeyActiveIndex(
        (prev) => ((prev % activeQuickKeyOptionsLength) + activeQuickKeyOptionsLength) %
          activeQuickKeyOptionsLength,
      );
    }, [activeQuickKeyOptionsLength]);

    const getNextQuickKeyIndex = useCallback(
      (current: number, step: number) => {
        if (activeQuickKeyOptionsLength <= 0) return 0;
        return (
          (current + step + activeQuickKeyOptionsLength) %
          activeQuickKeyOptionsLength
        );
      },
      [activeQuickKeyOptionsLength],
    );

    // 从当前光标前的连续文本片段中查找最近的快捷键，例如 abc@doc 会触发 @。
    const detectQuickKey = useCallback(
      (text: string, cursorPos: number) => {
        const quickKeyChars = quickKeys.map((qk) => qk.key);
        if (quickKeyChars.length === 0) return;

        const textBeforeCursor = text.slice(0, cursorPos);
        let found = false;
        for (let i = textBeforeCursor.length - 1; i >= 0; i--) {
          const char = textBeforeCursor[i];
          if (/\s/.test(char)) break;
          if (quickKeyChars.includes(char)) {
            const keyword = textBeforeCursor.slice(i + 1);
            setActiveQuickKey(char);
            setQuickKeyKeyword(keyword);
            setQuickKeyTriggerIdx(i);
            setQuickKeyActiveIndex(0);
            onQuickKeyTrigger?.(char, keyword);
            found = true;
            break;
          }
        }

        if (!found && activeQuickKey) {
          const triggerChar = text[quickKeyTriggerIdx];
          const keyword = text.slice(quickKeyTriggerIdx + 1, cursorPos);
          if (
            quickKeyTriggerIdx < 0 ||
            !triggerChar ||
            !quickKeyChars.includes(triggerChar) ||
            /\s/.test(keyword)
          ) {
            setActiveQuickKey(null);
            setQuickKeyKeyword("");
            setQuickKeyTriggerIdx(-1);
            setQuickKeyActiveIndex(0);
          } else {
            setQuickKeyKeyword(keyword);
          }
        }
      },
      [activeQuickKey, onQuickKeyTrigger, quickKeyTriggerIdx, quickKeys],
    );

    // 选择快捷项后转成结构化 Mention；inline 模式会替换掉触发字符和关键词。
    const handleQuickKeySelect = useCallback(
      (option: any) => {
        const mentionType =
          activeQuickKeyConfig?.mentionType ||
          activeQuickKeyConfig?.tagType ||
          activeQuickKey ||
          "unknown";
        const newMention: MentionTag = {
          id: generateId(),
          label: option.label,
          type: mentionType,
          value: option.value ?? option.uri ?? option.name ?? option.label,
          uri: option.uri,
          targetId: option.targetId ?? option.skillNo ?? option.pluginNo,
          displayName: option.displayName ?? option.label,
          trigger: activeQuickKey || undefined,
          icon: option.icon,
          data: option,
        };
        activeQuickKeyConfig?.onSelect?.(option);

        if (inlineTags) {
          inlineInputRef.current?.insertMention(newMention, {
            start: quickKeyTriggerIdx,
            end: quickKeyTriggerIdx + 1 + quickKeyKeyword.length,
          });
        } else {
          setMentions([...mentions, newMention]);

          // 从输入框中删除快捷键字符和关键词
          if (quickKeyTriggerIdx >= 0) {
            const newValue =
              value.slice(0, quickKeyTriggerIdx) +
              value.slice(quickKeyTriggerIdx + 1 + quickKeyKeyword.length);
            setValue(newValue);
          }
        }

        setActiveQuickKey(null);
        setQuickKeyKeyword("");
        setQuickKeyTriggerIdx(-1);
        setQuickKeyActiveIndex(0);
        inlineTags
          ? inlineInputRef.current?.focus()
          : textareaRef.current?.focus();
      },
      [
        activeQuickKey,
        activeQuickKeyConfig,
        inlineTags,
        mentions,
        setMentions,
        value,
        quickKeyTriggerIdx,
        quickKeyKeyword,
        setValue,
      ],
    );

    // 处理输入框变化（含快捷键检测）
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      // 检测快捷键触发
      detectQuickKey(newValue, e.target.selectionStart || 0);
    };

    const handleInlineInputChange = (newValue: string) => {
      setValue(newValue);
      // contenteditable 的 selection 不一定和 React state 同步，需实时读取内部光标 offset。
      detectQuickKey(
        newValue,
        inlineInputRef.current?.getCursorOffset() || newValue.length,
      );
    };

    const handleQuickKeyByText = (text: string, cursorPos: number) => {
      const quickKeyChars = quickKeys.map((qk) => qk.key);
      if (quickKeyChars.length === 0) return;

      const textBeforeCursor = text.slice(0, cursorPos);

      // 检测刚输入的字符是否是快捷键
      const lastChar = textBeforeCursor[cursorPos - 1];
      if (lastChar && quickKeyChars.includes(lastChar)) {
        // 上下键导航后会触发 keyup，此时光标前仍是同一个快捷键字符；
        // 如果重复重置 activeIndex，视觉上会“移动一下又回到第一项”。
        if (activeQuickKey === lastChar && quickKeyTriggerIdx === cursorPos - 1) {
          return;
        }
        setActiveQuickKey(lastChar);
        setQuickKeyKeyword("");
        setQuickKeyTriggerIdx(cursorPos - 1);
        setQuickKeyActiveIndex(0);
        onQuickKeyTrigger?.(lastChar, "");
      }
    };

    // onChange 负责更新搜索词；onKeyUp 兜底捕获刚输入的快捷键字符。
    const isQuickListControlKey = (key: string) =>
      key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === "Escape";

    const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (activeQuickKey && isQuickListControlKey(e.key)) return;
      const target = e.target as HTMLTextAreaElement;
      handleQuickKeyByText(target.value || "", target.selectionStart || 0);
    };

    const handleInlineKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (activeQuickKey && isQuickListControlKey(e.key)) return;
      // inline 输入区不是 textarea，不能读 event.target.value，只能从 ref 取纯文本。
      const text = inlineInputRef.current?.getText() || "";
      const cursorPos =
        inlineInputRef.current?.getCursorOffset() || text.length;
      handleQuickKeyByText(text, cursorPos);
    };

    // 处理提交
    const handleSubmit = () => {
      // 生成期间允许继续编辑下一条消息，但停止按钮仍代表当前唯一可执行动作。
      if (loading) return;
      if (!value.trim() && files.length === 0 && mentions.length === 0) {
        message.warning("请输入消息或选择文件");
        return;
      }

      const successFiles = files.filter((file) => file.status === "success");
      const uploadingFiles = files.filter(
        (file) => file.status === "uploading",
      );

      if (uploadingFiles.length > 0) {
        Modal.confirm({
          title: "文件上传中",
          content: `还有 ${uploadingFiles.length} 个文件正在上传，是否继续发送？`,
          okText: "发送已完成的",
          cancelText: "等待全部完成",
          onOk: () => {
            const formattedFiles = formatFilesForServer(successFiles);
            if (onSubmit) {
              onSubmit(createSubmitData(formattedFiles));
            }

            setValue("");
            setMentions([]);
            setFiles((prev) => prev.filter((f) => f.status !== "success"));
          },
          onCancel: () => {
            message.info("请等待文件上传完成后再发送");
          },
        });
      } else {
        const formattedFiles = formatFilesForServer(successFiles);

        if (onSubmit) {
          onSubmit(createSubmitData(formattedFiles));
        }

        setValue("");
        setMentions([]);
        setFiles((prev) => prev.filter((f) => f.status !== "success"));
      }
    };

    // 格式化文件列表为服务端格式
    const formatFilesForServer = (fileList: LocalFile[]): ServerFile[] => {
      return fileList
        .filter((file) => file.status === "success" && file.response)
        .map((file) => {
          const responseData = normalizeUploadResponse(file.response);

          return {
            fileName: responseData.fileName || file.name,
            fileId: responseData.fileId || responseData.id || "",
            tempUrl:
              responseData.tempUrl ||
              responseData.fileUrl ||
              responseData.url ||
              "",
            type: responseData.fileType || responseData.type || getExt(file),
            size: responseData.fileSize || responseData.size || file.size,
            mimeType: responseData.mimeType || file.type,
            raw: {
              ...responseData,
            },
          };
        });
    };

    // 处理回车键
    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      const quickKeyOptions = activeQuickKeyConfig?.options || [];

      if (activeQuickKey && quickKeyOptions.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setQuickKeyActiveIndex((prev) => getNextQuickKeyIndex(prev, 1));
          return;
        }

        if (e.key === "ArrowUp") {
          e.preventDefault();
          setQuickKeyActiveIndex((prev) => getNextQuickKeyIndex(prev, -1));
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();
          handleQuickKeySelect(
            quickKeyOptions[
              Math.min(quickKeyActiveIndex, quickKeyOptions.length - 1)
            ],
          );
          return;
        }

        if (e.key === "Escape") {
          e.preventDefault();
          setActiveQuickKey(null);
          setQuickKeyKeyword("");
          setQuickKeyTriggerIdx(-1);
          setQuickKeyActiveIndex(0);
          return;
        }
      }

      if (e.key === "Enter" && !e.shiftKey && !activeQuickKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    // 渲染文件列表
    const renderFileList = () => {
      if (files.length === 0) return null;

      return (
        <div className={styles.contextFiles}>
          {files.map((file) => (
            <FileGallery
              key={file.id}
              file={file}
              size="small"
              removable
              onRemove={handleRemoveFile}
            />
          ))}
        </div>
      );
    };

    const containerClass = `${styles.container} ${isDragOver ? "drag-over" : ""}`;
    const uploading = files.some((f) => f.status === "uploading");
    const controlsDisabled = disabled || uploading || loading;
    const inputDisabled = disabled || uploading;
    const hasContent = value.trim().length > 0;
    const hasFiles = files.some((f) => f.status === "success");
    const hasMentions = mentions.length > 0;
    const canSend = hasContent || hasFiles || hasMentions;

    // 定义按钮组件
    const SendButton = (props: any) => {
      if (sendButtonRender) {
        return (
          <>
            {sendButtonRender({
              loading,
              disabled: uploading || disabled,
              canSend,
              onSend: handleSubmit,
              onStop,
            })}
          </>
        );
      }
      return (
        <Tooltip title={loading ? "停止生成" : "发送消息"}>
          <button
            className={`${styles.iconButton} ${styles.sendButton} ${loading ? "stop" : ""}`}
            onClick={loading ? onStop : handleSubmit}
            disabled={uploading || disabled}
            aria-label={loading ? "停止生成" : "发送消息"}
            {...props}
          >
            {loading ? <LoadingOutlined spin /> : <ArrowUpOutlined />}
          </button>
        </Tooltip>
      );
    };

    const ClearButton = (props: any) => (
      <Popconfirm
        title="确定要清空聊天记录吗?"
        onConfirm={onClear}
        placement="top"
        okText="确定"
        cancelText="取消"
        disabled={controlsDisabled}
      >
        <button
          className={`${styles.iconButton} ${styles.clearButton}`}
          disabled={controlsDisabled}
          aria-label="清空聊天记录"
          {...props}
        >
          <ClearOutlined />
        </button>
      </Popconfirm>
    );

    const actionsComponents: ActionsComponents = {
      SendButton,
      ClearButton,
    };

    // 渲染插槽内容
    const renderSlot = (
      slot:
        | React.ReactNode
        | false
        | ((
            oriNode: React.ReactNode,
            info: { components: ActionsComponents },
          ) => React.ReactNode | false)
        | undefined,
      oriNode: React.ReactNode,
    ): React.ReactNode | null => {
      if (slot === false) return null;
      if (slot === undefined) return oriNode;
      if (typeof slot === "function") {
        const result = slot(oriNode, { components: actionsComponents });
        return result === false ? null : result;
      }
      return slot;
    };

    const headerNode = renderSlot(header, false);
    const fileListNode = files.length > 0 ? renderFileList() : null;

    // 默认后缀仅保留发送/停止按钮；上传与清空统一放到底部 actionBar，方便业务扩展左侧按钮组。
    const defaultSuffix = (
      <div className={styles.buttonGroup}>
        {sendButtonRender ? (
          sendButtonRender({
            loading,
            disabled: uploading || disabled,
            canSend,
            onSend: handleSubmit,
            onStop,
          })
        ) : (
          <Tooltip title={loading ? "停止生成" : "发送消息"}>
            <button
              className={`${styles.iconButton} ${styles.sendButton} ${loading ? "stop" : ""}`}
              onClick={loading ? onStop : handleSubmit}
              disabled={uploading || disabled}
              aria-label={loading ? "停止生成" : "发送消息"}
            >
              {loading ? <LoadingOutlined spin /> : <ArrowUpOutlined />}
            </button>
          </Tooltip>
        )}
      </div>
    );

    const defaultActionBar = (
      <div className={styles.actionBar}>
        <div className={styles.actionBarLeft}>
          {renderSlot(footerLeft, false)}
        </div>
        <div className={styles.actionBarRight}>
          {clearBtnShow && <ClearButton />}
          {renderSlot(suffix, defaultSuffix)}
        </div>
      </div>
    );

    return (
      <div
        ref={containerRef}
        className={containerClass}
        onDragOver={draggable ? handleDragOver : undefined}
        onDragLeave={draggable ? handleDragLeave : undefined}
        onDrop={draggable ? handleDrop : undefined}
      >
        <div className={styles.mainArea}>
          <div className={styles.senderWrap}>
            {(headerNode || fileListNode) && (
              <div className={styles.contextBar}>
                {/* 头部插槽与附件共享上下文行，便于知识库、文件等输入上下文统一展示。 */}
                {headerNode && (
                  <div className={styles.contextHeader}>{headerNode}</div>
                )}
                {fileListNode}
              </div>
            )}

            {/* textarea 模式下保留历史标签展示；inlineTags 模式标签直接混排在输入区内 */}
            {!inlineTags && (
              <InputTags mentions={mentions} onRemove={handleRemoveMention} />
            )}

            <div className={styles.inputAndButtons}>
              {/* 前缀插槽 */}
              {renderSlot(prefix, false)}

              {inlineTags ? (
                <InlineTagInput
                  ref={inlineInputRef}
                  value={value}
                  mentions={mentions}
                  defaultInputParts={defaultInputParts}
                  placeholder={placeholder}
                  disabled={inputDisabled}
                  onChange={handleInlineInputChange}
                  onMentionsChange={setMentions}
                  onKeyDown={handleKeyDown as any}
                  onKeyUp={handleInlineKeyUp}
                  renderMention={renderMention ?? renderTag}
                />
              ) : (
                <div className={styles.textAreaWrapper}>
                  <Input.TextArea
                    ref={textareaRef}
                    className={styles.textArea}
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    placeholder={placeholder}
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    style={{
                      border: "none",
                      boxShadow: "none",
                      outline: "none",
                      padding: 0,
                    }}
                  />
                </div>
              )}
            </div>

            {/* 快捷键弹出列表 */}
            {activeQuickKey && activeQuickKeyConfig && (
              <div className={styles.quickListWrapper}>
                <QuickList
                  config={activeQuickKeyConfig}
                  activeIndex={quickKeyActiveIndex}
                  onSelect={handleQuickKeySelect}
                  onClose={() => {
                    setActiveQuickKey(null);
                    setQuickKeyKeyword("");
                    setQuickKeyTriggerIdx(-1);
                    setQuickKeyActiveIndex(0);
                  }}
                  visible={true}
                />
              </div>
            )}

            {/* 底部操作行：左侧上传/业务按钮，右侧清空/发送按钮 */}
            {footer === false
              ? defaultActionBar
              : renderSlot(footer, defaultActionBar)}
          </div>
        </div>

        {/* 提示文案 */}
        {!footer && (
          <div className={styles.tip}>
            内容由AI生成,无法确保真实准确,仅供参考
          </div>
        )}
      </div>
    );
  },
);

XAdkSender.displayName = "XAdkSender";
export default XAdkSender;
